import { getApolloClient } from "src/clients/apollo-client";
import { BULK_GET_MFA_STATUS_QUERY } from "src/common/gql-operations";
import { NETWORK_ONLY } from "src/common/constants";
import { TenantID } from "src/common/enums";
import { BulkGetMfaStatusData, BulkGetMfaStatusVariables, MfaCredentialStatus } from "src/models/bulk-get-mfa-status";
import { AuthgardItemResult, AuthgardResultStatus, partitionAuthgardResults } from "src/models/authgard-item-result";
import { logger } from "src/logger";

import initialMetricsPublisher from 'src/metrics';
import * as KatalMetrics from "@amzn/katal-metrics";

const cloudWatchDimensions = [new KatalMetrics.Metric.String('provider', 'mfa-status-provider')];
const additionalMetricsContext = new KatalMetrics.Context({cloudWatchDimensions});
const graphqlClientMetricsPublisher =
    initialMetricsPublisher.newChildActionPublisherForMethod('graphql-client', additionalMetricsContext);

const MAX_PRINCIPALS_PER_CALL = 50;

/**
 * Fetches Authgard MFA status for a batch of principals via bulkGetMfaStatus.
 *
 * Returns a map from principalArn to a per-principal {@link AuthgardItemResult}: `success` carries
 * that principal's credentials (one entry per credential type it holds, e.g. SCAN2AUTH and/or
 * FIDO2); `failure` carries the per-principal error code; `unknown` marks a principal the response
 * never returned. Every requested arn is present in the map. The UI shows both `failure` and
 * `unknown` as "MFA status unavailable" rather than a misleading "not enrolled".
 * 
 * @param principalArns the principals to look up.
 * @return map of principalArn -> {@link AuthgardItemResult} of its credentials.
 * @throws if more than 50 principals are requested, or the request fails at the transport level.
 */
export const getMfaStatuses = async (
    principalArns: string[],
): Promise<Map<string, AuthgardItemResult<MfaCredentialStatus[]>>> => {
    const resultByArn = new Map<string, AuthgardItemResult<MfaCredentialStatus[]>>();
    if (principalArns.length === 0) {
        return resultByArn;
    }
    if (principalArns.length > MAX_PRINCIPALS_PER_CALL) {
        // Too many principals is a caller bug — page the reads instead of letting PCS reject it.
        throw new Error(`getMfaStatuses received ${principalArns.length} principals; the max per call is ` +
            `${MAX_PRINCIPALS_PER_CALL} (PrincipalArnList @length in FITPrincipalControlServiceModel).`);
    }
    // Default every arn to "unknown"; a success or failure entry below overrides it. An arn left
    // unknown is one the response never mentioned (a PCS contract gap) — we don't fabricate a status.
    principalArns.forEach(arn => resultByArn.set(arn, { status: AuthgardResultStatus.UNKNOWN }));

    try {
        logger.info(`Calling bulkGetMfaStatus for ${principalArns.length} principal(s)`);
        graphqlClientMetricsPublisher.publishCounterMonitor('bulk-get-mfa-status.INVOCATION', 1);

        const response = await getApolloClient().query<BulkGetMfaStatusData, BulkGetMfaStatusVariables>({
            query: BULK_GET_MFA_STATUS_QUERY,
            fetchPolicy: NETWORK_ONLY, // don't cache — MFA status changes on admin actions
            variables: {
                bulkGetMfaStatusInput: {
                    tenantId: TenantID.AFTX,
                    principalArns,
                },
            },
        });

        const results = response.data?.bulkGetMfaStatus?.results ?? [];
        for (const entry of results) {
            if (entry.success) {
                resultByArn.set(entry.principalArn, { status: AuthgardResultStatus.SUCCESS, value: entry.success.credentials ?? [] });
            } else if (entry.failure) {
                resultByArn.set(entry.principalArn, {
                    status: AuthgardResultStatus.FAILURE,
                    errorCode: entry.failure.errorCode,
                    errorMessage: entry.failure.errorMessage,
                });
            }
        }

        // One pass groups the results by status; emit a per-reason metric and log the identities in
        // each bucket (with the error results for failures) so the specific principals — and why they
        // failed — are greppable when debugging.
        const { successes, failures, unknown } = partitionAuthgardResults(resultByArn);
        if (failures.length > 0) {
            graphqlClientMetricsPublisher.publishCounterMonitor('bulk-get-mfa-status.LOOKUP_FAILURE', failures.length);
            logger.warn(`bulkGetMfaStatus: ${failures.length} lookup failure(s) — ` +
                failures.map(f => `${f.principalArn}: ${f.errorCode} (${f.errorMessage})`).join('; '));
        }
        if (unknown.length > 0) {
            graphqlClientMetricsPublisher.publishCounterMonitor('bulk-get-mfa-status.MISSING_RESULT', unknown.length);
            logger.warn(`bulkGetMfaStatus: no result returned for ${unknown.length} principal(s) — ${unknown.join(', ')}`);
        }
        if (successes.length > 0) {
            graphqlClientMetricsPublisher.publishCounterMonitor('bulk-get-mfa-status.LOOKUP_SUCCESS', successes.length);
        }
        logger.info(`bulkGetMfaStatus resolved ${successes.length} of ${principalArns.length} principal(s)` +
            (successes.length > 0 ? ` — ${successes.map(s => s.principalArn).join(', ')}` : ''));

        graphqlClientMetricsPublisher.publishCounterMonitor('bulk-get-mfa-status.SUCCESS', 1);
        return resultByArn;
    } catch (e) {
        // A total request failure is an unknown outcome for the whole batch — bubble it up so the
        // caller shows "couldn't load" rather than a map full of fabricated "not enrolled" entries.
        logger.error(`Failed to get MFA status for ${principalArns.length} principal(s)`, e as Error);
        graphqlClientMetricsPublisher.publishCounterMonitor('bulk-get-mfa-status.ERROR', 1);
        throw e;
    }
};
